/**
 * AdminSnapshotsTab — Snapshots mensais por cliente + envio de e-mail.
 * Sprint 6: Extraído de AdminPanel.jsx.
 * Sprint 10: Adicionado busca/filtro por cliente, filtro por período, paginação.
 */
import { useState, useMemo } from "react";
import PropTypes from "prop-types";
import { Card, STitle, Badge, LIGHT, fmt } from "./adminUtils.jsx";
import { RADIUS } from "../shared/theme.js";
import EmptyState from "./components/EmptyState.jsx";

const PAGE_SIZE = 30;

export default function AdminSnapshotsTab({
  dynamicClients,
  allSnapshots,
  emailSending,
  emailResults,
  handleSendEmail,
  onRunAnalysis,
  analysisLoading,
  T,
}) {
  const [search, setSearch] = useState("");
  const [periodFilter, setPeriodFilter] = useState("all");
  const [page, setPage] = useState(0);

  // ── Unique periods for filter dropdown ──
  const periods = useMemo(() => {
    const set = new Set(allSnapshots.map((s) => s.period).filter(Boolean));
    return Array.from(set).sort((a, b) => b.localeCompare(a));
  }, [allSnapshots]);

  // ── Client name lookup ──
  const clientMap = useMemo(() => {
    const m = {};
    dynamicClients.forEach((c) => {
      m[c.id] = c.name || c.id;
    });
    return m;
  }, [dynamicClients]);

  // ── Filtered snapshots ──
  const filtered = useMemo(() => {
    let list = allSnapshots;

    if (search.trim()) {
      const q = search.toLowerCase().trim();
      list = list.filter((s) => {
        const name = (clientMap[s.client_id] || s.client_id).toLowerCase();
        return name.includes(q) || (s.client_id || "").toLowerCase().includes(q);
      });
    }

    if (periodFilter !== "all") {
      list = list.filter((s) => s.period === periodFilter);
    }

    return list;
  }, [allSnapshots, search, periodFilter, clientMap]);

  // Reset page on filter change
  const setSearchAndReset = (v) => {
    setSearch(v);
    setPage(0);
  };
  const setPeriodAndReset = (v) => {
    setPeriodFilter(v);
    setPage(0);
  };

  const totalPages = Math.ceil(filtered.length / PAGE_SIZE);
  const pageSnapshots = filtered.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);

  // ── Latest period unsent count ──
  const latestPeriodUnsent = useMemo(() => {
    if (periods.length === 0) return { period: null, count: 0 };
    const latest = periods[0]; // already sorted desc
    const latestSnaps = allSnapshots.filter((s) => s.period === latest);
    const unsent = latestSnaps.filter((s) => !s.email_sent_at);
    return { period: latest, count: unsent.length, total: latestSnaps.length };
  }, [allSnapshots, periods]);

  const fmtRet = (v) => (v != null ? `${Number(v).toFixed(2)}%` : "–");
  const fmtV = (v) => (v != null ? fmt(Number(v)) : "–");

  const inputStyle = {
    padding: "7px 12px",
    borderRadius: RADIUS.sm,
    border: `1px solid ${T.border}`,
    fontSize: 12,
    color: T.text,
    background: T.card,
    outline: "none",
  };
  const selectStyle = { ...inputStyle, cursor: "pointer", minWidth: 120 };
  const btnStyle = {
    padding: "6px 14px",
    borderRadius: RADIUS.sm,
    border: `1px solid ${T.border}`,
    background: T.bg,
    color: T.text,
    fontSize: 12,
    fontWeight: 600,
    cursor: "pointer",
  };

  return (
    <div>
      {/* Cabeçalho com botão "Enviar para todos" */}
      <Card T={T} style={{ marginBottom: 16 }}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: 12,
          }}
        >
          <div>
            <STitle
              title="Snapshots Mensais por Cliente"
              sub="Histórico de posições carregadas · clique em ✉️ para enviar relatório por e-mail"
              T={T}
            />
          </div>
          <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
            {emailResults && (
              <div style={{ fontSize: 12, color: "#16A34A", fontWeight: 600 }}>
                ✅ {emailResults.sent} e-mail(s) enviado(s)
              </div>
            )}
            <button
              onClick={() => handleSendEmail(null, latestPeriodUnsent.period)}
              disabled={emailSending === "all" || latestPeriodUnsent.count === 0}
              title={
                latestPeriodUnsent.count > 0
                  ? `Enviar relatório ${latestPeriodUnsent.period} para ${latestPeriodUnsent.count} cliente(s) ainda não enviado(s)`
                  : "Todos os relatórios do último período já foram enviados"
              }
              style={{
                padding: "9px 18px",
                borderRadius: RADIUS.sm,
                border: "none",
                background:
                  emailSending === "all"
                    ? "#94A3B8"
                    : latestPeriodUnsent.count === 0
                      ? "#94A3B8"
                      : "#16A34A",
                color: "#fff",
                fontSize: 13,
                fontWeight: 700,
                cursor:
                  emailSending === "all" || latestPeriodUnsent.count === 0 ? "default" : "pointer",
                display: "flex",
                alignItems: "center",
                gap: 8,
                opacity: latestPeriodUnsent.count === 0 ? 0.5 : 1,
              }}
            >
              {emailSending === "all"
                ? "⏳ Enviando…"
                : latestPeriodUnsent.count > 0
                  ? `✉️ Enviar ${latestPeriodUnsent.count} não-enviado(s) · ${latestPeriodUnsent.period}`
                  : "✅ Todos enviados"}
            </button>
          </div>
        </div>
      </Card>

      <Card T={T}>
        {/* Summary KPIs */}
        <div
          style={{
            display: "flex",
            gap: 14,
            marginBottom: 16,
            flexWrap: "wrap",
          }}
        >
          {[
            { label: "Total Snapshots", v: allSnapshots.length, color: "#2563EB" },
            {
              label: "Clientes com Snap",
              v: new Set(allSnapshots.map((s) => s.client_id)).size,
              color: "#16A34A",
            },
            { label: "Períodos", v: periods.length, color: "#7C3AED" },
          ].map((s) => (
            <div
              key={s.label}
              style={{
                background: T.bg,
                border: `1px solid ${T.border}`,
                borderRadius: RADIUS.md,
                padding: "10px 18px",
                minWidth: 130,
              }}
            >
              <div style={{ fontSize: 11, color: T.sub, marginBottom: 3 }}>{s.label}</div>
              <div style={{ fontSize: 18, fontWeight: 800, color: s.color }}>{s.v}</div>
            </div>
          ))}
        </div>

        {/* Filter bar */}
        <div
          style={{
            display: "flex",
            gap: 10,
            marginBottom: 14,
            flexWrap: "wrap",
            alignItems: "center",
          }}
        >
          <input
            type="text"
            placeholder="Buscar cliente…"
            value={search}
            onChange={(e) => setSearchAndReset(e.target.value)}
            style={{ ...inputStyle, flex: "1 1 180px", minWidth: 160 }}
          />
          <select
            value={periodFilter}
            onChange={(e) => setPeriodAndReset(e.target.value)}
            style={selectStyle}
          >
            <option value="all">Todos os períodos</option>
            {periods.map((p) => (
              <option key={p} value={p}>
                {p}
              </option>
            ))}
          </select>
          {(search || periodFilter !== "all") && (
            <span style={{ fontSize: 11, color: T.muted }}>
              {filtered.length} de {allSnapshots.length}
            </span>
          )}
        </div>

        {filtered.length === 0 ? (
          <EmptyState
            icon={allSnapshots.length === 0 ? "📷" : "🔍"}
            title={
              allSnapshots.length === 0
                ? "Nenhum snapshot encontrado"
                : "Nenhum snapshot corresponde aos filtros"
            }
            subtitle={
              allSnapshots.length === 0
                ? "Importe extratos na aba Importar Dados para gerar snapshots."
                : "Tente ajustar os filtros de busca."
            }
            T={T}
          />
        ) : (
          <div style={{ overflowX: "auto", WebkitOverflowScrolling: "touch" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
              <thead>
                <tr>
                  {[
                    "Cliente",
                    "Período",
                    "Patrimônio",
                    "Retorno Mês",
                    "% CDI 12M",
                    "Fonte",
                    "Análise",
                    "E-mail",
                  ].map((h) => (
                    <th
                      key={h}
                      scope="col"
                      style={{
                        textAlign: "left",
                        padding: "9px 12px",
                        borderBottom: `2px solid ${T.border}`,
                        color: T.sub,
                        fontSize: 11,
                        fontWeight: 700,
                        textTransform: "uppercase",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {pageSnapshots.map((s, i) => {
                  const clientName = clientMap[s.client_id] || s.client_id;
                  const retColor = (s.ret_month || 0) >= 0 ? "#16A34A" : "#DC2626";
                  const isSending = emailSending === s.client_id;
                  return (
                    <tr key={s.id} style={{ background: i % 2 === 0 ? T.row1 : T.row2 }}>
                      <td
                        style={{
                          padding: "10px 12px",
                          fontWeight: 700,
                          color: T.text,
                          whiteSpace: "nowrap",
                        }}
                      >
                        {clientName}
                      </td>
                      <td style={{ padding: "10px 12px" }}>
                        <Badge text={s.period || "–"} color={T.info || "#2563EB"} />
                      </td>
                      <td style={{ padding: "10px 12px", fontWeight: 800, color: T.text }}>
                        {fmtV(s.patrimonio_total)}
                      </td>
                      <td style={{ padding: "10px 12px", fontWeight: 700, color: retColor }}>
                        {fmtRet(s.ret_month)}
                      </td>
                      <td style={{ padding: "10px 12px" }}>
                        <Badge
                          text={
                            s.pct_cdi_12m != null ? `${Number(s.pct_cdi_12m).toFixed(0)}% CDI` : "–"
                          }
                          color={Number(s.pct_cdi_12m || 0) >= 100 ? "#16A34A" : "#D97706"}
                          bg={Number(s.pct_cdi_12m || 0) >= 100 ? "#DCFCE7" : "#FEF3C7"}
                        />
                      </td>
                      <td style={{ padding: "10px 12px" }}>
                        <Badge
                          text={s.source || "–"}
                          color={
                            s.source === "seed"
                              ? "#16A34A"
                              : s.source === "import"
                                ? "#2563EB"
                                : "#94A3B8"
                          }
                        />
                      </td>
                      <td style={{ padding: "10px 12px" }}>
                        {onRunAnalysis && (
                          <button
                            onClick={() => onRunAnalysis(s.client_id, s.period)}
                            disabled={analysisLoading === `${s.client_id}-${s.period}`}
                            title={`Gerar análise de portfólio ${s.period} para ${clientName}`}
                            style={{
                              padding: "5px 12px",
                              borderRadius: RADIUS.sm,
                              border: "none",
                              background:
                                analysisLoading === `${s.client_id}-${s.period}`
                                  ? "#94A3B8"
                                  : "#0F3460",
                              color: "#fff",
                              fontSize: 11,
                              fontWeight: 700,
                              cursor:
                                analysisLoading === `${s.client_id}-${s.period}`
                                  ? "wait"
                                  : "pointer",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {analysisLoading === `${s.client_id}-${s.period}`
                              ? "⏳…"
                              : "📊 Análise"}
                          </button>
                        )}
                      </td>
                      <td style={{ padding: "10px 12px" }}>
                        <button
                          onClick={() => handleSendEmail(s.client_id, s.period)}
                          disabled={!!emailSending}
                          title={`Enviar relatório ${s.period} para ${clientName}`}
                          style={{
                            padding: "5px 12px",
                            borderRadius: RADIUS.sm,
                            border: "none",
                            background: isSending ? "#94A3B8" : "#2563EB",
                            color: "#fff",
                            fontSize: 11,
                            fontWeight: 700,
                            cursor: emailSending ? "wait" : "pointer",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {isSending ? "⏳…" : "✉️ Enviar"}
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              gap: 8,
              marginTop: 14,
              paddingTop: 12,
              borderTop: `1px solid ${T.border}`,
            }}
          >
            <button
              onClick={() => setPage((p) => Math.max(0, p - 1))}
              disabled={page === 0}
              style={{ ...btnStyle, opacity: page === 0 ? 0.4 : 1 }}
            >
              ← Anterior
            </button>
            <span style={{ fontSize: 12, color: T.sub }}>
              {page + 1} / {totalPages}
            </span>
            <button
              onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
              disabled={page >= totalPages - 1}
              style={{ ...btnStyle, opacity: page >= totalPages - 1 ? 0.4 : 1 }}
            >
              Próxima →
            </button>
          </div>
        )}
      </Card>
    </div>
  );
}

AdminSnapshotsTab.propTypes = {
  dynamicClients: PropTypes.array.isRequired,
  allSnapshots: PropTypes.array.isRequired,
  emailSending: PropTypes.string,
  emailResults: PropTypes.object,
  handleSendEmail: PropTypes.func.isRequired,
  onRunAnalysis: PropTypes.func,
  analysisLoading: PropTypes.string,
  T: PropTypes.object.isRequired,
};
